import React, { useState, useEffect } from "react";
import { useUser } from "../UserContext";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Drawer,
  List,
  ListItemButton,
  Button,
  Avatar,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import NavItems, { navigationConfig, UserRole } from "./NavItems";

// Interfaces
interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url: string;
  user_access?: {
    tags?: string[];
    user_status?: {
      Verified?: string;
      Admin?: string;
      Active?: string;
    };
  };
}

interface BrandJson {
  brand_id: number;
  brand: string;
  avatar_url: string;
}

interface AccountJson {
  id: number;
  account: string;
  avatar_url: string;
  account_id?: number;
}

interface SidebarProps {
  onLogout: () => void;
  onaccountSelect: (accountId: number) => void;
  isExpanded: boolean;
}

// Styled Components
const drawerWidth = 240;

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  width: isExpanded ? drawerWidth : 60,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: isExpanded ? drawerWidth : 70,
    boxSizing: "border-box",
    backgroundColor: theme.palette?.custom?.sidebar || "#150218",
    color: theme.palette.text.tertiary,
  },
  "& .MuiListItemText-primary": {
    color: theme.palette.text.tertiary,
  },
}));

const WhiteSelect = styled(Select)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  "& .MuiSelect-icon": {
    color: theme.palette.text.secondary,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.text.secondary,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.text.secondary,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.text.secondary,
  },
}));

const WhiteInputLabel = styled(InputLabel)(({ theme }) => ({
  color: theme.palette.text.secondary,
  "&.Mui-focused": {
    color: theme.palette.text.secondary,
  },
}));

const PurpleButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#6B20F5",
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: "#553C9A",
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 70,
  height: 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Sidebar: React.FC<SidebarProps> = ({
  onLogout,
  onaccountSelect,
  isExpanded,
}) => {
  const {
    currentUser,
    setselectedAccountId,
    selectedAccountId,
    setSelectedBrandId,
    selectedBrandId,
    setSelectedAgencyID,
    handleUserSelect,
    handleAccountSelect,
    handleBrandSelect,
    selectedRoleId,
    setSelectedRoleId,
  } = useUser();

  const [users, setUsers] = useState<User[]>([]);
  const [brands, setBrands] = useState<BrandJson[]>([]);

  // Fetch users for Developer role
  useEffect(() => {
    const fetchUsers = async () => {
      if (currentUser?.role !== "Developer") return;

      try {
        const apiUrl = import.meta.env.VITE_API_URL;
        if (!apiUrl) return;

        const authToken =
          localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!authToken) return;

        const response = await axios.get(`${apiUrl}/users/users`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const activeUsers = response.data.filter(
          (user: User) => user.user_access?.user_status?.Active === "Yes"
        );
        setUsers(activeUsers);
      } catch (error: any) {
        if (error.response?.status === 403) return;
      }
    };

    if (currentUser?.role === "Developer") {
      fetchUsers();
    }
  }, [currentUser]);

  // Fetch user-specific data
  useEffect(() => {
    const fetchUserData = async () => {
      const userId = currentUser?.user_id || currentUser?.id;
      if (!userId) {
        console.log("No user ID available in currentUser:", currentUser);
        return;
      }

      try {
        const apiUrl = import.meta.env.VITE_API_URL;
        const authToken =
          localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!authToken) {
          console.log("No auth token found, skipping fetchUserData");
          return;
        }

        console.log("Fetching user data for ID:", userId);
        console.log("API URL:", apiUrl);

        const response = await axios.get(`${apiUrl}/users/${userId}/data`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        console.log("User data response:", {
          role: currentUser.role,
          hasRoleDataJson: !!response.data.role_data_json,
          roleDataLength: response.data.role_data_json?.length,
          responseData: response.data,
        });

        if (currentUser.role === "Brand" || currentUser.role === "Supplier") {
          const brandsData = response.data.role_data_json || [];
          console.log("Processing brands data:", brandsData);

          const transformedBrands = brandsData.map((item: any) => ({
            brand_id: item.brand_id,
            brand: item.brand,
            avatar_url: item.avatar_url || "",
          }));

          console.log("Transformed brands:", transformedBrands);
          setBrands(transformedBrands);

          if (transformedBrands.length > 0) {
            console.log(
              "Setting initial brand ID:",
              transformedBrands[0].brand_id
            );
            setSelectedBrandId(transformedBrands[0].brand_id);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        console.error("Error details:", {
          message: (error as any)?.message,
          response: (error as any)?.response?.data,
        });
      }
    };

    console.log("Sidebar useEffect triggered", {
      hasCurrentUser: !!currentUser,
      userRole: currentUser?.role,
      isDeveloper: currentUser?.role !== "Developer",
    });

    if (currentUser && currentUser.role !== "Developer") {
      fetchUserData();
    }
  }, [currentUser]);

  // Set initial selections based on role
  useEffect(() => {
    if (!currentUser) return;

    const parsedData = {
      Account: currentUser?.accounts_json || [],
      Owner: currentUser?.accounts_json || [],
      Agency: currentUser?.agencies_json || [],
    };

    if (currentUser?.user_id && selectedRoleId !== currentUser.user_id) {
      setSelectedRoleId(currentUser.user_id);
    }

    switch (currentUser.role) {
      case "Account":
      case "Owner":
        if (parsedData[currentUser.role].length > 0) {
          setselectedAccountId(parsedData[currentUser.role][0].account_id);
        }
        break;
      case "Agency":
        if (parsedData[currentUser.role].length > 0) {
          setSelectedAgencyID(parsedData[currentUser.role][0].agency_id);
        }
        break;
    }
  }, [currentUser, selectedRoleId]);

  const handleAccountSelectWrapper = (id: number) => {
    handleAccountSelect(id);
    onaccountSelect(id);
  };

  // Render navigation links based on user role
  const renderLinks = () => {
    if (!currentUser?.role) return null;
    const roleItems = navigationConfig[currentUser.role as UserRole];
    if (!roleItems) return null;

    return (
      <>
        {currentUser.role === "Developer" && (
          <Box sx={{ p: 2 }}>
            <FormControl fullWidth>
              <WhiteInputLabel>Select User</WhiteInputLabel>
              <WhiteSelect
                value={selectedRoleId || ""}
                onChange={(e) => handleUserSelect(Number(e.target.value))}
                label="Select User"
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </MenuItem>
                ))}
              </WhiteSelect>
            </FormControl>
          </Box>
        )}
        <NavItems items={roleItems} isExpanded={isExpanded} />
      </>
    );
  };

  // Render user selection dropdown
  const renderUserSelect = () => {
    if (!isExpanded || currentUser?.role !== "Developer") return null;

    return (
      <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
        <WhiteInputLabel id="user-select-label">Select User</WhiteInputLabel>
        <WhiteSelect
          labelId="user-select-label"
          value={currentUser?.user_id || ""}
          onChange={(e) => handleUserSelect(e.target.value as number)}
          label="Select User"
        >
          {users.length > 0 ? (
            users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                <Box display="flex" alignItems="center">
                  <Avatar
                    src={
                      user.avatar_url ||
                      "https://media.promoraven.com/app/placeholders/placeholder.jpg"
                    }
                    sx={{ width: 30, height: 30, mr: 1 }}
                  />
                  <Typography>{`${user.first_name} ${user.last_name}`}</Typography>
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem value="">No users available</MenuItem>
          )}
        </WhiteSelect>
      </FormControl>
    );
  };

  // Render account selection
  const renderAccountSelect = () => {
    if (currentUser?.role !== "Account" && currentUser?.role !== "Owner")
      return null;

    if (isExpanded) {
      return (
        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <WhiteInputLabel id="access-select-label">
            Select account
          </WhiteInputLabel>
          <WhiteSelect
            labelId="access-select-label"
            value={selectedAccountId || ""}
            onChange={(e) =>
              handleAccountSelectWrapper(e.target.value as number)
            }
            label="Select account"
          >
            {currentUser.accounts_json?.map((data: AccountJson) => (
              <MenuItem key={data.account_id} value={data.account_id}>
                <Box display="flex" alignItems="center">
                  <Avatar
                    src={
                      data.avatar_url ||
                      "https://media.promoraven.com/app/placeholders/placeholder.jpg"
                    }
                    sx={{ width: 30, height: 30, mr: 1 }}
                  />
                  <Typography>{data.account}</Typography>
                </Box>
              </MenuItem>
            ))}
          </WhiteSelect>
        </FormControl>
      );
    }
    return null;
  };

  // Render brand selection
  const renderBrandSelect = () => {
    if (currentUser?.role !== "Brand" && currentUser?.role !== "Supplier")
      return null;

    if (isExpanded) {
      return (
        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <WhiteInputLabel id="brand-select-label">
            Select Brand
          </WhiteInputLabel>
          <WhiteSelect
            labelId="brand-select-label"
            value={selectedBrandId || ""}
            onChange={(e) => handleBrandSelect(e.target.value as number)}
            label="Select Brand"
          >
            {brands.map((data: BrandJson) => (
              <MenuItem key={data.brand_id} value={data.brand_id}>
                <Box display="flex" alignItems="center">
                  <Avatar
                    src={
                      data.avatar_url ||
                      "https://media.promoraven.com/app/placeholders/placeholder.jpg"
                    }
                    sx={{ width: 30, height: 30, mr: 1 }}
                  />
                  <Typography>{data.brand}</Typography>
                </Box>
              </MenuItem>
            ))}
          </WhiteSelect>
        </FormControl>
      );
    }
    return null;
  };

  // Render logout button
  const renderLogout = () => {
    if (isExpanded) {
      return (
        <PurpleButton variant="contained" fullWidth onClick={onLogout}>
          Logout
        </PurpleButton>
      );
    }
    return (
      <IconBox>
        <ListItemButton sx={{ padding: 3 }}>
          <LogoutIcon onClick={onLogout} />
        </ListItemButton>
      </IconBox>
    );
  };

  return (
    <StyledDrawer variant="permanent" anchor="left" isExpanded={isExpanded}>
      <Box sx={{ p: 3.7, textAlign: "center" }} />
      <List>{renderLinks()}</List>
      <Box sx={{ mt: "auto", p: 2 }} />
      {renderUserSelect()}
      {renderAccountSelect()}
      {renderBrandSelect()}
      {renderLogout()}
    </StyledDrawer>
  );
};

export default Sidebar;
